/** A link's ⋯ menu — the same in the tour-wide list and the date-specific grid:
 *  **Open registration page ↗**, **Edit settings…**, **Make master now** (an active custom
 *  link without a date), a divider, then **Revoke link now…** — or **Restore link now** on a
 *  revoked link.
 *
 *  The "… now" items act on the server at once (Draft-Or-Now): a bolt icon and the
 *  immediate-action description, like `ImmediateActionButton`; revoke asks first. A
 *  deleted tour keeps revoke and restore (B2), and says why the others are unavailable. */
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TuneIcon from '@mui/icons-material/Tune';
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';

import type { TourSelfregLink } from '@/api/tours';
import { IMMEDIATE_DESCRIPTION } from '@/features/records/actions/ImmediateActionButton';

import { addressTail, canBecomeMaster } from './linkWords';

const immediate = { 'aria-description': IMMEDIATE_DESCRIPTION, 'data-immediate-action': '' } as Record<string, string>;

export interface LinkMenuAvailability {
  /** Settings and Make master need a live tour with an operator (B2: 404 otherwise). */
  editable: boolean;
  /** Why not, when not editable. */
  editReason?: string;
  /** A make-master / restore is on its way: its item waits. */
  makingMaster?: boolean;
  restoring?: boolean;
}

export function LinkActionsMenu({
  link,
  anchorEl,
  onClose,
  availability,
  onEdit,
  onMakeMaster,
  onRevoke,
  onRestore,
}: {
  link: TourSelfregLink | null;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  availability: LinkMenuAvailability;
  onEdit: (link: TourSelfregLink) => void;
  onMakeMaster: (link: TourSelfregLink) => void;
  onRevoke: (link: TourSelfregLink) => void;
  onRestore: (link: TourSelfregLink) => void;
}) {
  const open = Boolean(link && anchorEl);
  const revoked = Boolean(link?.revoked_ts);
  const reason = availability.editable ? undefined : availability.editReason;
  const run = (fn: (l: TourSelfregLink) => void) => () => {
    if (!link) return;
    onClose();
    fn(link);
  };
  return (
    <Menu
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      MenuListProps={{ 'aria-label': link ? `Actions of link ${addressTail(link)}` : 'Link actions', dense: true }}
      data-selfreg-menu
    >
      {link && [
        <MenuItem key="open" component="a" href={link.link} target="_blank" rel="noopener noreferrer" onClick={onClose}>
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open registration page</ListItemText>
        </MenuItem>,
        <MenuItem key="edit" onClick={run(onEdit)} disabled={!availability.editable}>
          <ListItemIcon>
            <TuneIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Edit settings…" secondary={reason} />
        </MenuItem>,
        ...(canBecomeMaster(link)
          ? [
              <MenuItem
                key="master"
                onClick={run(onMakeMaster)}
                disabled={!availability.editable || Boolean(availability.makingMaster)}
                {...immediate}
              >
                <ListItemIcon>
                  <BoltOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Make master now" secondary={reason} />
              </MenuItem>,
            ]
          : []),
        <Divider key="divider" />,
        revoked ? (
          <MenuItem key="restore" onClick={run(onRestore)} disabled={Boolean(availability.restoring)} {...immediate}>
            <ListItemIcon>
              <BoltOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Restore link now</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem key="revoke" onClick={run(onRevoke)} sx={{ color: 'error.main' }} {...immediate}>
            <ListItemIcon sx={{ color: 'error.main' }}>
              <BoltOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Revoke link now…</ListItemText>
          </MenuItem>
        ),
      ]}
    </Menu>
  );
}
